<template>
  <!-- Labeled, searchable multi-select that looks like the drawer's other fields.
       Closed: a select-like box showing "All" or the chosen values. Open: a type-
       to-search box + a checkbox list of ALL values (filtered by the search).
       Parent owns the array (toggleFilter adds/removes, clearFilter empties). -->
  <div class="l2-f">
    <span>{{ label }}<span v-if="selected.length" class="ms-count"> ({{ selected.length }})</span></span>
    <div class="ms" :class="{ 'ms-open': open }">
      <button type="button" class="ms-box" @click="open = !open">
        <span class="ms-val" :class="{ 'ms-ph': !selected.length }">{{ selected.length ? selected.join(', ') : 'All' }}</span>
        <span class="ms-chev">{{ open ? '▴' : '▾' }}</span>
      </button>
      <div v-if="open" class="ms-pop">
        <input ref="q" class="ms-search" type="text" v-model="q" placeholder="Type to search…" />
        <div class="ms-list">
          <label v-for="o in filtered" :key="optVal(o)" class="ms-opt" :title="optTip(o)">
            <input type="checkbox" :checked="selected.indexOf(optVal(o)) >= 0" @change="$emit('toggle', optVal(o))" />
            <span>{{ optLabel(o) }}</span>
          </label>
          <div v-if="!options.length" class="ms-empty">No values available.</div>
          <div v-else-if="!filtered.length" class="ms-empty">No match.</div>
        </div>
        <div class="ms-foot">
          <button v-if="selected.length" type="button" class="ms-clear" @click="$emit('clear')">Clear ({{ selected.length }})</button>
          <span class="ms-spacer"></span>
          <button type="button" class="ms-done" @click="open = false">Done</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FilterSlicer",
  props: {
    label: { type: String, default: "" },
    options: { type: Array, default: () => [] },
    selected: { type: Array, default: () => [] },
    tips: { type: Object, default: () => ({}) },   // optional per-value hover text: { <optionValue>: "tooltip" }
  },
  data() {
    return { open: false, q: "" };
  },
  computed: {
    // Partial-search narrowing (case-insensitive substring, matches the visible label).
    filtered() {
      const s = this.q.trim().toLowerCase();
      const list = this.options || [];
      if (!s) return list;
      return list.filter((o) => String(this.optLabel(o)).toLowerCase().indexOf(s) >= 0);
    },
  },
  methods: {
    // Options may be plain strings OR { value, label } objects. The filter value is always optVal;
    // optLabel is what's shown (e.g. "AL105 — Aardvark Clay & Supply" while the value stays "AL105").
    optVal(o) { return o && typeof o === "object" ? o.value : o; },
    optLabel(o) { return o && typeof o === "object" ? o.label : o; },
    optTip(o) { return (this.tips && this.tips[this.optVal(o)]) || ""; },
    // Close the open dropdown when the user clicks anywhere OUTSIDE this slicer (so pressing "Done" is
    // optional). $el is the slicer root (.l2-f); the box AND the popover both live inside it, so a click
    // within either stays open.
    onDocMouseDown(e) {
      if (this.$el && !this.$el.contains(e.target)) this.open = false;
    },
  },
  watch: {
    open(v) {
      if (v) {
        this.$nextTick(() => { if (this.$refs.q) this.$refs.q.focus(); });
        // Capture phase so this fires before the card/grid handlers below it.
        document.addEventListener("mousedown", this.onDocMouseDown, true);
      } else {
        this.q = "";
        document.removeEventListener("mousedown", this.onDocMouseDown, true);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocMouseDown, true);
  },
};
</script>

<style scoped>
.ms { position: relative; }
.ms-box { width: 100%; height: 38px; display: flex; align-items: center; justify-content: space-between; gap: 8px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 10px; background: #fff; font-size: 14px; cursor: pointer; text-align: left; }
.ms-open .ms-box { border-color: #9e2436; box-shadow: 0 0 0 2px rgba(158,36,54,.12); }
.ms-val { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #222; }
.ms-val.ms-ph { color: #999; }
.ms-chev { color: #888; font-size: 11px; flex: 0 0 auto; }
.ms-count { color: #9e2436; font-weight: 800; }
.ms-pop { margin-top: 6px; border: 1px solid #e0e0e4; border-radius: 6px; background: #fff; box-shadow: 0 6px 18px rgba(0,0,0,.12); padding: 8px; }
.ms-search { width: 100%; height: 32px; border: 1px solid #d4d4d8; border-radius: 6px; padding: 0 9px; font-size: 13px; margin-bottom: 7px; }
.ms-search:focus { outline: none; border-color: #9e2436; }
.ms-list { max-height: 168px; overflow-y: auto; overflow-x: hidden; display: flex; flex-direction: column; gap: 6px; }
.ms-opt { display: flex; align-items: flex-start; gap: 8px; color: #333; cursor: pointer; text-align: left; }
/* Explicit checkbox size overrides the portal's global `input { width:100% }`, which
   otherwise stretched the checkbox to full width and pushed the label off-screen. */
.ms-opt input { width: 16px; height: 16px; min-width: 16px; margin: 1px 0 0; padding: 0; accent-color: #9e2436; flex: 0 0 auto; }
.ms-opt span { flex: 1 1 auto; min-width: 0; text-align: left; font-size: 12.5px; line-height: 1.35; word-break: break-word; }
.ms-empty { font-size: 12px; color: #999; padding: 3px 1px; }
.ms-foot { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.ms-spacer { flex: 1; }
.ms-clear { border: 0; background: none; color: #9e2436; font-size: 12px; font-weight: 700; cursor: pointer; text-decoration: underline; padding: 0; }
.ms-done { border: 1px solid #d4d4d8; background: #f6f6f7; color: #333; font-size: 12px; font-weight: 700; border-radius: 6px; padding: 4px 12px; cursor: pointer; }
.ms-done:hover { background: #ececef; }
/* Dark mode (Sales Insights) — the closed box + the popup were white; darken so the Status/etc. slicers match. */
html.dark-mode .ms-box { background: #11151a; border-color: #3a414a; }
html.dark-mode .ms-val { color: #e3e7eb; }
html.dark-mode .ms-val.ms-ph { color: #8a929c; }
html.dark-mode .ms-chev { color: #9aa3ad; }
html.dark-mode .ms-pop { background: #1f242b; border-color: #3a414a; box-shadow: 0 6px 18px rgba(0,0,0,.5); }
html.dark-mode .ms-search { background: #11151a; border-color: #3a414a; color: #e3e7eb; }
html.dark-mode .ms-opt { color: #dfe3e8; }
html.dark-mode .ms-empty { color: #8a929c; }
html.dark-mode .ms-done { background: #2a313a; border-color: #4a525c; color: #dfe3e8; }
html.dark-mode .ms-done:hover { background: #333c46; }
</style>
